import { useCallback, useEffect, useRef, useState } from 'react';
import {
  openShortcutsPageViaExtension,
  probeShortcuts,
  type AggregateShortcutsState,
} from '@/lib/extensionBridge';
import type { ShortcutInfo } from '@/types/extension';

export type ShortcutsState =
  | { kind: 'loading' }
  | { kind: 'not_installed' }
  | { kind: 'installed_old' }
  | { kind: 'supported'; shortcuts: ShortcutInfo[] }
  | { kind: 'error'; message: string };

export interface UseShortcutsResult {
  state: ShortcutsState;
  refetch: () => void;
  /** Opens chrome://extensions/shortcuts via the extension. Resolves false
   *  if no extension acknowledges (caller should keep CopyShortcutsLink
   *  visible as the always-available fallback). */
  openShortcutsPage: () => Promise<boolean>;
}

/**
 * Source of truth for "what does Chrome think the extension's bindings
 * are right now". Fetched on mount and again on every window focus —
 * users typically open chrome://extensions/shortcuts in a new tab,
 * change a binding, and switch back, and focus is the only consistent
 * signal we get for "they probably just changed something".
 *
 * Stale-request guard:
 *   The user can trigger a refetch (focus, explicit click) while a prior
 *   request is still in flight. Without a guard, an OLD response can land
 *   AFTER a newer one and overwrite the fresh state. We use a monotonic
 *   generation counter; only the most-recently-issued request is allowed
 *   to call setState.
 */
export function useShortcuts(): UseShortcutsResult {
  const [state, setState] = useState<ShortcutsState>({ kind: 'loading' });
  const generationRef = useRef(0);
  const aliveRef = useRef(true);

  const fetchNow = useCallback(async () => {
    const myGen = ++generationRef.current;
    // Only show 'loading' on the very first request — subsequent refetches
    // keep the previous result visible (otherwise focus refetch would
    // unmount the section briefly every tab-switch, which feels janky).
    setState((prev) => (prev.kind === 'loading' ? prev : prev));

    let result: AggregateShortcutsState | { state: 'error'; message: string };
    try {
      result = await probeShortcuts();
    } catch (err) {
      result = { state: 'error', message: err instanceof Error ? err.message : String(err) };
    }

    if (!aliveRef.current) return;
    if (myGen !== generationRef.current) return; // stale — newer request in flight

    switch (result.state) {
      case 'supported':
        setState({ kind: 'supported', shortcuts: result.shortcuts });
        break;
      case 'installed_old':
        setState({ kind: 'installed_old' });
        break;
      case 'not_installed':
        setState({ kind: 'not_installed' });
        break;
      case 'error':
        setState({ kind: 'error', message: result.message });
        break;
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    void fetchNow();
    const onFocus = () => { void fetchNow(); };
    window.addEventListener('focus', onFocus);
    return () => {
      aliveRef.current = false;
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchNow]);

  const refetch = useCallback(() => { void fetchNow(); }, [fetchNow]);

  const openShortcutsPage = useCallback(async () => {
    const opened = await openShortcutsPageViaExtension();
    // After the user (presumably) tweaks the binding and tabs back, the
    // window-focus listener will refetch automatically. No explicit poll.
    return opened;
  }, []);

  return { state, refetch, openShortcutsPage };
}
